import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import { useActivityLog } from '../../../hooks/useActivityLog';
import '../ProductManagement/ProductForm.css';
import './WarehousesList.css';

const WarehousesList = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const logActivity = useActivityLog();
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);

  // Modal state
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  // Form data
  const [warehouseFormData, setWarehouseFormData] = useState({
    name: '',
    code: '',
    address: '',
    totalCapacity: '',
    manager: '',
    phone: '',
    email: '',
    active: true
  });

  const [locationFormData, setLocationFormData] = useState({
    warehouseId: '',
    name: '',
    locationCode: '',
    locationType: 'shelf',
    zone: '',
    aisle: '',
    rack: '',
    shelf: '',
    bin: '',
    level: '',
    capacity: '',
    capacityUnit: 'units',
    maxWeight: '',
    maxVolume: '',
    temperatureControlled: false,
    temperatureMin: '',
    temperatureMax: '',
    humidityControlled: false,
    hazmatApproved: false,
    foodGrade: false,
    isActive: true,
    isPickable: true,
    isReceivable: true,
    pickSequence: ''
  });

  useEffect(() => {
    loadWarehouses();
  }, [organization]);

  useEffect(() => {
    applyFilters();
  }, [warehouses, searchTerm, showInactiveOnly]);

  const loadWarehouses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await inventoryApi.getWarehouses(organization);
      const data = Array.isArray(response) ? response : (response?.data || []);
      setWarehouses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStorageLocations = async (warehouseId) => {
    try {
      const response = await inventoryApi.getStorageLocations(organization, warehouseId);
      const data = Array.isArray(response) ? response : (response?.data || []);
      setStorageLocations(data);
    } catch (err) {
      console.error('Error loading storage locations:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...warehouses];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(w =>
        w.name?.toLowerCase().includes(term) ||
        w.code?.toLowerCase().includes(term) ||
        w.address?.toLowerCase().includes(term) ||
        w.manager?.toLowerCase().includes(term)
      );
    }

    if (showInactiveOnly) {
      filtered = filtered.filter(w => !w.active);
    }

    setFilteredWarehouses(filtered);
  };

  const handleCreateWarehouse = () => {
    setEditingWarehouse(null);
    setWarehouseFormData({
      name: '',
      code: '',
      address: '',
      totalCapacity: '',
      manager: '',
      phone: '',
      email: '',
      active: true
    });
    setShowWarehouseModal(true);
  };

  const handleEditWarehouse = (warehouse) => {
    setEditingWarehouse(warehouse);
    const totalCapacity = warehouse.total_capacity || warehouse.totalCapacity || '';

    setWarehouseFormData({
      name: warehouse.name || '',
      code: warehouse.code || '',
      address: warehouse.address || '',
      totalCapacity: totalCapacity,
      manager: warehouse.manager || '',
      phone: warehouse.phone || '',
      email: warehouse.email || '',
      active: warehouse.active !== false
    });
    setShowWarehouseModal(true);
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    if (!window.confirm(t('inventory.warehouse_delete_confirm'))) {
      return;
    }

    try {
      await inventoryApi.deleteWarehouse(warehouseId);
      await loadWarehouses();
      logActivity(organization, 'WAREHOUSE_DELETED', { warehouseId });
    } catch (err) {
      alert(t('inventory.warehouse_delete_error') + ' ' + err.message);
    }
  };

  const handleSubmitWarehouse = async (e) => {
    e.preventDefault();

    try {
      const data = {
        organization,
        name: warehouseFormData.name,
        code: warehouseFormData.code,
        address: warehouseFormData.address,
        total_capacity: parseFloat(warehouseFormData.totalCapacity) || null,
        manager: warehouseFormData.manager,
        phone: warehouseFormData.phone,
        email: warehouseFormData.email,
        active: warehouseFormData.active
      };

      if (editingWarehouse) {
        await inventoryApi.updateWarehouse(editingWarehouse.id, data);
        logActivity(organization, 'WAREHOUSE_UPDATED', { name: data.name, code: data.code });
      } else {
        await inventoryApi.createWarehouse(data);
        logActivity(organization, 'WAREHOUSE_CREATED', { name: data.name, code: data.code });
      }

      setShowWarehouseModal(false);
      await loadWarehouses();
    } catch (err) {
      alert(t('inventory.warehouse_save_error') + ' ' + err.message);
    }
  };

  const handleViewLocations = async (warehouse) => {
    setSelectedWarehouse(warehouse);
    await loadStorageLocations(warehouse.id);
  };

  const handleCreateLocation = () => {
    setEditingLocation(null);
    setLocationFormData({
      warehouseId: selectedWarehouse?.id || '',
      name: '',
      locationCode: '',
      locationType: 'shelf',
      zone: '',
      aisle: '',
      rack: '',
      shelf: '',
      bin: '',
      level: '',
      capacity: '',
      capacityUnit: 'units',
      maxWeight: '',
      maxVolume: '',
      temperatureControlled: false,
      temperatureMin: '',
      temperatureMax: '',
      humidityControlled: false,
      hazmatApproved: false,
      foodGrade: false,
      isActive: true,
      isPickable: true,
      isReceivable: true,
      pickSequence: ''
    });
    setShowLocationModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);

    const warehouseId = location.warehouse_id || location.warehouseId;
    const locationCode = location.location_code || location.locationCode;
    const locationType = location.location_type || location.locationType || 'shelf';
    const capacityUnit = location.capacity_unit || location.capacityUnit || 'units';
    const maxWeight = location.max_weight || location.maxWeight;
    const maxVolume = location.max_volume || location.maxVolume;
    const tempControlled = location.temperature_controlled || location.temperatureControlled || false;
    const tempMin = location.temperature_min || location.temperatureMin;
    const tempMax = location.temperature_max || location.temperatureMax;
    const humidityControlled = location.humidity_controlled || location.humidityControlled || false;
    const hazmatApproved = location.hazmat_approved || location.hazmatApproved || false;
    const foodGrade = location.food_grade || location.foodGrade || false;
    const isActive = location.is_active !== undefined ? location.is_active : (location.isActive !== false);
    const isPickable = location.is_pickable !== undefined ? location.is_pickable : (location.isPickable !== false);
    const isReceivable = location.is_receivable !== undefined ? location.is_receivable : (location.isReceivable !== false);
    const pickSequence = location.pick_sequence || location.pickSequence;

    setLocationFormData({
      warehouseId: warehouseId,
      name: location.name || '',
      locationCode: locationCode || '',
      locationType: locationType,
      zone: location.zone || '',
      aisle: location.aisle || '',
      rack: location.rack || '',
      shelf: location.shelf || '',
      bin: location.bin || '',
      level: location.level || '',
      capacity: location.capacity || '',
      capacityUnit: capacityUnit,
      maxWeight: maxWeight || '',
      maxVolume: maxVolume || '',
      temperatureControlled: tempControlled,
      temperatureMin: tempMin || '',
      temperatureMax: tempMax || '',
      humidityControlled: humidityControlled,
      hazmatApproved: hazmatApproved,
      foodGrade: foodGrade,
      isActive: isActive,
      isPickable: isPickable,
      isReceivable: isReceivable,
      pickSequence: pickSequence || ''
    });
    setShowLocationModal(true);
  };

  const handleSubmitLocation = async (e) => {
    e.preventDefault();

    try {
      const data = {
        warehouse_id: parseInt(locationFormData.warehouseId),
        organization,
        name: locationFormData.name,
        location_code: locationFormData.locationCode,
        location_type: locationFormData.locationType,
        zone: locationFormData.zone || null,
        aisle: locationFormData.aisle || null,
        rack: locationFormData.rack || null,
        shelf: locationFormData.shelf || null,
        bin: locationFormData.bin || null,
        level: parseInt(locationFormData.level) || null,
        capacity: parseFloat(locationFormData.capacity) || null,
        capacity_unit: locationFormData.capacityUnit,
        max_weight: parseFloat(locationFormData.maxWeight) || null,
        max_volume: parseFloat(locationFormData.maxVolume) || null,
        temperature_controlled: locationFormData.temperatureControlled,
        temperature_min: parseFloat(locationFormData.temperatureMin) || null,
        temperature_max: parseFloat(locationFormData.temperatureMax) || null,
        humidity_controlled: locationFormData.humidityControlled,
        hazmat_approved: locationFormData.hazmatApproved,
        food_grade: locationFormData.foodGrade,
        is_active: locationFormData.isActive,
        is_pickable: locationFormData.isPickable,
        is_receivable: locationFormData.isReceivable,
        pick_sequence: parseInt(locationFormData.pickSequence) || null
      };

      if (editingLocation) {
        await inventoryApi.updateStorageLocation(editingLocation.id, data);
        logActivity(organization, 'STORAGE_LOCATION_UPDATED', { name: data.name, location_code: data.location_code });
      } else {
        await inventoryApi.createStorageLocation(data);
        logActivity(organization, 'STORAGE_LOCATION_CREATED', { name: data.name, location_code: data.location_code });
      }

      setShowLocationModal(false);
      await loadStorageLocations(selectedWarehouse.id);
    } catch (err) {
      alert('Error saving storage location: ' + err.message);
    }
  };

  const formatCapacity = (warehouse) => {
    const total = warehouse.total_capacity || warehouse.totalCapacity || 0;
    const used = warehouse.used_capacity || warehouse.usedCapacity || 0;
    const percentage = total > 0 ? ((used / total) * 100).toFixed(1) : 0;
    return `${used.toFixed(1)} / ${total.toFixed(1)} m³ (${percentage}%)`;
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.warehouses_loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-page-card">
        <div className="pm-error-banner">
          <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>❌</span>
          <div>
            <strong>{t('inventory.warehouses_error_prefix')}</strong> {error}
          </div>
        </div>
        <button className="pm-btn-primary" onClick={loadWarehouses}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.warehouses_title')}</h2>
        <div className="pm-header-actions">
          <button onClick={handleCreateWarehouse} className="pm-btn-primary">
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.create_warehouse_button')}</span>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_warehouses_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <label className="pm-toggle-row-inline">
            <input
              type="checkbox"
              role="switch"
              checked={showInactiveOnly}
              onChange={(e) => setShowInactiveOnly(e.target.checked)}
            />
            {t('inventory.show_inactive_only')}
          </label>
          <span className="pm-filter-stats">
            {t('inventory.warehouses_filter_stats', { filtered: filteredWarehouses.length, total: warehouses.length })}
          </span>
        </div>
      </div>

      {/* Warehouses Table */}
      {filteredWarehouses.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">🏭</div>
          <h3>{t('inventory.no_warehouses_found')}</h3>
          <button onClick={handleCreateWarehouse} className="pm-btn-primary">
            <span aria-hidden="true">➕</span>
            {t('inventory.create_warehouse_button')}
          </button>
        </div>
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table" role="grid">
            <thead>
              <tr>
                <th>{t('inventory.warehouse_table_name')}</th>
                <th>{t('inventory.warehouse_table_code')}</th>
                <th>{t('inventory.warehouse_table_location')}</th>
                <th>{t('inventory.warehouse_table_manager')}</th>
                <th>{t('inventory.warehouse_table_capacity')}</th>
                <th>{t('inventory.warehouse_table_status')}</th>
                <th style={{ width: '120px' }}>{t('inventory.warehouse_table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredWarehouses.map(warehouse => (
                <tr key={warehouse.id}>
                  <td className="pm-warehouse-name">{warehouse.name}</td>
                  <td>{warehouse.code || '-'}</td>
                  <td className="pm-address-cell">{warehouse.address || '-'}</td>
                  <td>{warehouse.manager || '-'}</td>
                  <td>{formatCapacity(warehouse)}</td>
                  <td>
                    <span className={`pm-status-badge ${warehouse.active ? 'active' : 'inactive'}`}>
                      {warehouse.active ? t('inventory.warehouse_status_active') : t('inventory.warehouse_status_inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="pm-row-actions">
                      <button
                        onClick={() => handleViewLocations(warehouse)}
                        className="pm-row-btn view"
                        title={t('inventory.warehouse_tooltip_view_locations')}
                      >
                        📍
                      </button>
                      <button
                        onClick={() => handleEditWarehouse(warehouse)}
                        className="pm-row-btn edit"
                        title={t('inventory.warehouse_tooltip_edit')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteWarehouse(warehouse.id)}
                        className="pm-row-btn delete"
                        title={t('inventory.warehouse_tooltip_delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Storage Locations Panel */}
      {selectedWarehouse && (
        <div className="pm-locations-panel">
          <div className="pm-locations-header">
            <h3>{t('inventory.storage_locations_title')} {selectedWarehouse.name}</h3>
            <div className="pm-header-actions">
              <button onClick={handleCreateLocation} className="pm-btn-primary">
                <span aria-hidden="true">➕</span>
                <span>{t('inventory.add_location_button')}</span>
              </button>
              <button onClick={() => setSelectedWarehouse(null)} className="pm-btn-secondary">
                <span aria-hidden="true">✕</span>
                <span className="desktop-only">{t('inventory.close_button')}</span>
              </button>
            </div>
          </div>

          {storageLocations.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
              <p>{t('inventory.no_locations_found')}</p>
              <button onClick={handleCreateLocation} className="pm-btn-primary">
                <span aria-hidden="true">➕</span>
                {t('inventory.add_location_button')}
              </button>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table" role="grid">
                <thead>
                  <tr>
                    <th>{t('inventory.location_table_code')}</th>
                    <th>{t('inventory.location_table_name')}</th>
                    <th>{t('inventory.location_table_type')}</th>
                    <th>{t('inventory.location_table_location')}</th>
                    <th>{t('inventory.location_table_capacity')}</th>
                    <th>{t('inventory.location_table_features')}</th>
                    <th>{t('inventory.location_table_status')}</th>
                    <th style={{ width: '80px' }}>{t('inventory.location_table_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {storageLocations.map(location => {
                    const locationCode = location.location_code || location.locationCode;
                    const locationType = location.location_type || location.locationType;
                    const capacityUnit = location.capacity_unit || location.capacityUnit;
                    const isActive = location.is_active !== undefined ? location.is_active : location.isActive;
                    const tempControlled = location.temperature_controlled || location.temperatureControlled;
                    const hazmat = location.hazmat_approved || location.hazmatApproved;
                    const foodGrade = location.food_grade || location.foodGrade;

                    const locationPath = [
                      location.zone,
                      location.aisle,
                      location.rack,
                      location.shelf,
                      location.bin
                    ].filter(Boolean).join('-') || '-';

                    const features = [];
                    if (tempControlled) features.push('🌡️');
                    if (hazmat) features.push('⚠️');
                    if (foodGrade) features.push('🍎');

                    return (
                      <tr key={location.id}>
                        <td className="pm-location-code">{locationCode || '-'}</td>
                        <td>{location.name}</td>
                        <td>
                          <span className="pm-type-badge">{locationType}</span>
                        </td>
                        <td>{locationPath}</td>
                        <td>
                          {location.capacity ? `${location.capacity} ${capacityUnit}` : '-'}
                        </td>
                        <td className="pm-features-cell">
                          {features.length > 0 ? features.join(' ') : '-'}
                        </td>
                        <td>
                          <span className={`pm-status-badge ${isActive ? 'active' : 'inactive'}`}>
                            {isActive ? t('inventory.warehouse_status_active') : t('inventory.warehouse_status_inactive')}
                          </span>
                        </td>
                        <td>
                          <div className="pm-row-actions">
                            <button
                              onClick={() => handleEditLocation(location)}
                              className="pm-row-btn edit"
                              title={t('inventory.warehouse_tooltip_edit')}
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Warehouse Modal */}
      {showWarehouseModal && (
        <div className="pm-modal-overlay" onClick={() => setShowWarehouseModal(false)}>
          <div className="pm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingWarehouse ? t('inventory.edit_warehouse_title') : t('inventory.create_warehouse_title')}</h2>
              <button onClick={() => setShowWarehouseModal(false)} className="pm-modal-close">×</button>
            </div>

            <form onSubmit={handleSubmitWarehouse}>
              <div className="pm-modal-body">
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_name_label')} <span className="pm-required">*</span></label>
                    <input
                      type="text"
                      value={warehouseFormData.name}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_code_label')}</label>
                    <input
                      type="text"
                      value={warehouseFormData.code}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, code: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group full-width">
                    <label>{t('inventory.warehouse_address_label')}</label>
                    <textarea
                      value={warehouseFormData.address}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, address: e.target.value})}
                      rows="2"
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_capacity_label')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={warehouseFormData.totalCapacity}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, totalCapacity: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_manager_label')}</label>
                    <input
                      type="text"
                      value={warehouseFormData.manager}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, manager: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_phone_label')}</label>
                    <input
                      type="tel"
                      value={warehouseFormData.phone}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, phone: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.warehouse_email_label')}</label>
                    <input
                      type="email"
                      value={warehouseFormData.email}
                      onChange={(e) => setWarehouseFormData({...warehouseFormData, email: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={warehouseFormData.active}
                        onChange={(e) => setWarehouseFormData({...warehouseFormData, active: e.target.checked})}
                      />
                      {t('inventory.warehouse_active_label')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button type="button" onClick={() => setShowWarehouseModal(false)} className="pm-btn-secondary">
                  {t('inventory.cancel_button')}
                </button>
                <button type="submit" className="pm-btn-primary">
                  {editingWarehouse ? t('inventory.update_button') : t('inventory.create_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Storage Location Modal */}
      {showLocationModal && (
        <div className="pm-modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="pm-modal-content pm-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingLocation ? t('inventory.edit_location_title') : t('inventory.create_location_title')}</h2>
              <button onClick={() => setShowLocationModal(false)} className="pm-modal-close">×</button>
            </div>

            <form onSubmit={handleSubmitLocation}>
              <div className="pm-modal-body">
                <div className="pm-subsection-title">{t('inventory.location_section_basic')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.location_name_label')} <span className="pm-required">*</span></label>
                    <input
                      type="text"
                      value={locationFormData.name}
                      onChange={(e) => setLocationFormData({...locationFormData, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_code_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.locationCode}
                      onChange={(e) => setLocationFormData({...locationFormData, locationCode: e.target.value})}
                      placeholder={t('inventory.location_code_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_type_label')}</label>
                    <select
                      value={locationFormData.locationType}
                      onChange={(e) => setLocationFormData({...locationFormData, locationType: e.target.value})}
                    >
                      <option value="zone">{t('inventory.location_type_zone')}</option>
                      <option value="aisle">{t('inventory.location_type_aisle')}</option>
                      <option value="rack">{t('inventory.location_type_rack')}</option>
                      <option value="shelf">{t('inventory.location_type_shelf')}</option>
                      <option value="bin">{t('inventory.location_type_bin')}</option>
                      <option value="pallet">{t('inventory.location_type_pallet')}</option>
                    </select>
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.location_section_path')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.location_zone_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.zone}
                      onChange={(e) => setLocationFormData({...locationFormData, zone: e.target.value})}
                      placeholder={t('inventory.location_zone_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_aisle_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.aisle}
                      onChange={(e) => setLocationFormData({...locationFormData, aisle: e.target.value})}
                      placeholder={t('inventory.location_aisle_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_rack_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.rack}
                      onChange={(e) => setLocationFormData({...locationFormData, rack: e.target.value})}
                      placeholder={t('inventory.location_rack_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_shelf_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.shelf}
                      onChange={(e) => setLocationFormData({...locationFormData, shelf: e.target.value})}
                      placeholder={t('inventory.location_shelf_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_bin_label')}</label>
                    <input
                      type="text"
                      value={locationFormData.bin}
                      onChange={(e) => setLocationFormData({...locationFormData, bin: e.target.value})}
                      placeholder={t('inventory.location_bin_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_level_label')}</label>
                    <input
                      type="number"
                      value={locationFormData.level}
                      onChange={(e) => setLocationFormData({...locationFormData, level: e.target.value})}
                    />
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.location_section_capacity')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.location_capacity_label')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={locationFormData.capacity}
                      onChange={(e) => setLocationFormData({...locationFormData, capacity: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_capacity_unit_label')}</label>
                    <select
                      value={locationFormData.capacityUnit}
                      onChange={(e) => setLocationFormData({...locationFormData, capacityUnit: e.target.value})}
                    >
                      <option value="units">{t('inventory.location_capacity_units')}</option>
                      <option value="pallets">{t('inventory.location_capacity_pallets')}</option>
                      <option value="cubic_meters">{t('inventory.location_capacity_cubic_meters')}</option>
                      <option value="kg">{t('inventory.location_capacity_kg')}</option>
                    </select>
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_max_weight_label')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={locationFormData.maxWeight}
                      onChange={(e) => setLocationFormData({...locationFormData, maxWeight: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_max_volume_label')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={locationFormData.maxVolume}
                      onChange={(e) => setLocationFormData({...locationFormData, maxVolume: e.target.value})}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.location_pick_sequence_label')}</label>
                    <input
                      type="number"
                      value={locationFormData.pickSequence}
                      onChange={(e) => setLocationFormData({...locationFormData, pickSequence: e.target.value})}
                      placeholder={t('inventory.location_pick_sequence_placeholder')}
                    />
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.location_section_features')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.temperatureControlled}
                        onChange={(e) => setLocationFormData({...locationFormData, temperatureControlled: e.target.checked})}
                      />
                      {t('inventory.location_temp_controlled_label')}
                    </label>
                  </div>
                </div>

                {locationFormData.temperatureControlled && (
                  <div className="pm-form-grid">
                    <div className="pm-form-group">
                      <label>{t('inventory.location_temp_min_label')}</label>
                      <input
                        type="number"
                        step="0.1"
                        value={locationFormData.temperatureMin}
                        onChange={(e) => setLocationFormData({...locationFormData, temperatureMin: e.target.value})}
                      />
                    </div>

                    <div className="pm-form-group">
                      <label>{t('inventory.location_temp_max_label')}</label>
                      <input
                        type="number"
                        step="0.1"
                        value={locationFormData.temperatureMax}
                        onChange={(e) => setLocationFormData({...locationFormData, temperatureMax: e.target.value})}
                      />
                    </div>
                  </div>
                )}

                <div className="pm-form-grid">
                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.humidityControlled}
                        onChange={(e) => setLocationFormData({...locationFormData, humidityControlled: e.target.checked})}
                      />
                      {t('inventory.location_humidity_controlled_label')}
                    </label>
                  </div>

                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.hazmatApproved}
                        onChange={(e) => setLocationFormData({...locationFormData, hazmatApproved: e.target.checked})}
                      />
                      {t('inventory.location_hazmat_label')}
                    </label>
                  </div>

                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.foodGrade}
                        onChange={(e) => setLocationFormData({...locationFormData, foodGrade: e.target.checked})}
                      />
                      {t('inventory.location_food_grade_label')}
                    </label>
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.location_section_operational')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.isActive}
                        onChange={(e) => setLocationFormData({...locationFormData, isActive: e.target.checked})}
                      />
                      {t('inventory.location_active_label')}
                    </label>
                  </div>

                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.isPickable}
                        onChange={(e) => setLocationFormData({...locationFormData, isPickable: e.target.checked})}
                      />
                      {t('inventory.location_pickable_label')}
                    </label>
                  </div>

                  <div className="pm-form-group full-width">
                    <label className="pm-toggle-row-inline">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={locationFormData.isReceivable}
                        onChange={(e) => setLocationFormData({...locationFormData, isReceivable: e.target.checked})}
                      />
                      {t('inventory.location_receivable_label')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button type="button" onClick={() => setShowLocationModal(false)} className="pm-btn-secondary">
                  {t('inventory.cancel_button')}
                </button>
                <button type="submit" className="pm-btn-primary">
                  {editingLocation ? t('inventory.update_button') : t('inventory.create_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehousesList;
